/**
 * ExploreCollections component renders a carousel to explore different collections.
 *
 * @function ExploreCollections
 * @param {Object} props - The component's props.
 * @param {Object} props.sanity - The data object containing configuration and content.
 * @param {string} props.sanity.backgroundType - Background type ('Solid', 'Image', 'Gradient').
 * @param {string} props.sanity.backgroundColor - Solid color for the background.
 * @param {Object} props.sanity.backgroundImage - The background image object.
 * @param {Object} props.sanity.backgroundGradient - The background gradient object.
 * @param {string} props.sanity.gridType - Type of grid for the carousel.
 * @param {string} props.sanity._type - Internal type used for styling.
 * @result {JSX.Element} A section element with class 'section_librero collections_wrapper', and other dynamic classes and styles based on the sanity object. The component also contains logic for handling scrolling and resizing events, and rendering buttons for navigation.
 * @example
 * <ExploreCollections sanity={sanityData} />
 */
import React, {useCallback, useEffect, useState} from 'react';

import useEmblaCarousel from 'embla-carousel-react';
import {Link, Image} from '@shopify/hydrogen';
import '../../assets/css/carousel.css';
import GridCarousel from '../carousel/GridCarousel.client';
import {getPath} from '../../lib/methods';
import ImageLoad from '../general/ImageLoad.client';

export default function ExploreCollections({sanity}) {
  const [browserWidth, setBrowserWidth] = useState(0);

  /**
   * @const [viewportRef, embla] - Embla Carousel instance.
   */
  const [viewportRef, embla] = useEmblaCarousel({
    containScroll: 'scrollSnap',
    slidesToScroll: 1,
    inViewThreshold: 1,
    skipSnaps: false,
    selectedClass: '',
    dragFree: true,
    align: 'start',
    loop: false,
  });

  /**
   * @function scrollPrev
   * @description Scrolls to the previous item in the carousel.
   */
  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  
  /**
   * @function scrollNext
   * @description Scrolls to the next item in the carousel.
   */
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);

  /**
   * @const [prevBtnEnabled, setPrevBtnEnabled] - State for the previous button's enabled status.
   */
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);

  /**
   * @const [nextBtnEnabled, setNextBtnEnabled] - State for the next button's enabled status.
   */
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  /**
   * @const [scrollSnaps, setScrollSnaps] - Scroll snaps state.
   */
  const [scrollSnaps, setScrollSnaps] = useState([]);

  /**
   * @const [selectedIndex, setSelectedIndex] - Selected index state.
   */
  const [selectedIndex, setSelectedIndex] = useState(0);

  /**
   * @const [width, setWidth] - Width state.
   */
  const [width, setWidth] = useState(0);

  /**
   * @const [isMobile, setIsMobile] - Mobile view state.
   */
  const [isMobile, setIsMobile] = useState(width < 768 ? true : false);

  /**
   * @function scrollTo
   * @param {number} index - Index to scroll to.
   * @description Scrolls to the specified index in the carousel.
   */
  const scrollTo = useCallback(
    (index) => embla && embla.scrollTo(index),
    [embla],
  );


  useEffect(()=>{
    const width = window.innerWidth;
    setBrowserWidth(width);
  },[])


  /**
   * @function onSelect
   * @description Handles carousel selection changes.
   */
  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    setScrollSnaps(embla.scrollSnapList());
    embla.on('select', onSelect);
  }, [embla, setScrollSnaps, onSelect]);

  const handleWindowSizeChange = async () => {
    await setWidth(window.innerWidth);
    const mobile = width < 768 ? true : false;
    setIsMobile(mobile);
  };
  useEffect(() => {
    handleWindowSizeChange();
  });
  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  });

    /**
   * @function PrevButton
   * @param {Object} props - The component props.
   * @param {boolean} props.enabled - Whether the button is enabled.
   * @param {function} props.onClick - The click handler for the button.
   * @returns {JSX.Element} The PrevButton component.
   */
  const PrevButton = ({enabled, onClick}) => (
    <button
      className="btn-prev absolute left-0  top-2/4 bottom-52 flex items-center justify-center rounded-full h-10 w-10 bg-white cursor-pointer"
      onClick={onClick}
      disabled={!enabled}
    >
      <svg
        className="embla__button__svg h-5 w-5  fill-purple-120"
        viewBox="137.718 -1.001 366.563 644"
      >
        <path d="M428.36 12.5c16.67-16.67 43.76-16.67 60.42 0 16.67 16.67 16.67 43.76 0 60.42L241.7 320c148.25 148.24 230.61 230.6 247.08 247.08 16.67 16.66 16.67 43.75 0 60.42-16.67 16.66-43.76 16.67-60.42 0-27.72-27.71-249.45-249.37-277.16-277.08a42.308 42.308 0 0 1-12.48-30.34c0-11.1 4.1-22.05 12.48-30.42C206.63 234.23 400.64 40.21 428.36 12.5z" />
      </svg>
    </button>
  );

  /**
   * @function NextButton
   * @param {Object} props - The component props.
   * @param {boolean} props.enabled - Whether the button is enabled.
   * @param {function} props.onClick - The click handler for the button.
   * @returns {JSX.Element} The NextButton component.
   */
  const NextButton = ({enabled, onClick}) => {
    return (
      <button
        className={` ${
          !enabled ? 'fill-gray-300' : 'fill-purple-120'
        } btn-next absolute right-0 bottom-52 top-2/4  flex items-center justify-center rounded-full h-10 w-10 bg-white cursor-pointer`}
        onClick={onClick}
        disabled={!enabled}
      >
        <svg
          className={`embla__button__svg  h-5 w-5 fill-purple-120  ${
            !enabled ? 'fill-gray-300' : 'fill-purple-120'
          }`}
          viewBox="0 0 238.003 238.003"
        >
          <path d="M181.776 107.719L78.705 4.648c-6.198-6.198-16.273-6.198-22.47 0s-6.198 16.273 0 22.47l91.883 91.883-91.883 91.883c-6.198 6.198-6.198 16.273 0 22.47s16.273 6.198 22.47 0l103.071-103.039a15.741 15.741 0 0 0 4.64-11.283c0-4.13-1.526-8.199-4.64-11.313z" />
        </svg>
      </button>
    );
  };

/**
 * @function generateTextblock
 * @param {Array|String} textBlock - An array of elements containing text or a string.
 * @returns {String} - The concatenated text generated from the input.
 * @description Takes an array of elements containing text or a string and concatenates them into a single string.
 * @example
 * const input = [{ children: [{ text: 'Hello ' }] }, { children: [{ text: 'world!' }] }];
 * const output = generateTextblock(input); // "Hello world!"
 */
  const generateTextblock = (textBlock) => {
    let text = '';
    if (Array.isArray(textBlock)) {
      textBlock.forEach((element) => {
        element.children.forEach((item) => {
          text += item.text;
        });
      });
    } else {
      text = textBlock;
    }

    return text;
  };

  const toLowerCase = (string) => {
    let rstring = string;
    if (rstring) {
      rstring = rstring.toLowerCase();
      rstring = rstring.replace(/ /g, '_');
    }
    if (rstring.includes('-')) {
      rstring = rstring.replace(/-/g, '');
    }
    return rstring;
  };
  return (
    <section
      className={`section_librero collections_wrapper ${toLowerCase(
        sanity._type + '_' + sanity.gridType,
      )}`}
      style={{
        background:
          sanity.backgroundType == 'Solid'
            ? sanity.backgroundColor
            : sanity.backgroundType == 'Image'
            ? `url(${sanity.backgroundImage.asset.url})`
            : sanity.backgroundType == 'Gradient'
            ? `linear-gradient(90deg, ${sanity?.backgroundGradient?.gradientFrom} 0%, ${sanity?.backgroundGradient?.gradientTo} 100%)`
            : '#00000',
        backgroundSize: 'cover',
      }}
    >
      {sanity.gridType == 'Display - Colecciones pequeño' ||
      sanity.gridType == 'Display - Colecciones Grande' ? (
        <div className="container">
          <div
            className={`md:grid md:gap-4 excollection_small_container xs:flex ${
              sanity.gridType == 'Display - Colecciones pequeño'
                ? 'md:grid-cols-2 xs:flex-col'
                : sanity.gridType == 'Display - Colecciones Grande'
                ? 'md:grid-cols-3 xs:grid-cols-1'
                : 'md:grid-cols-2 xs:grid-cols-1'
            }`}
          >
            <div className="mt-8 xs:mt-0 xs:mx-6 md:pl-28 flex align-center flex-col justify-center excollection_small_container_data">
              <h2 className="section-title font-semibold text-3xl xs:text-center">
                {sanity.title}
              </h2>
              <p className="my-4 xs:text-center xs:mb-0 section-subtitle">
                {sanity.description}
              </p>
              <div className="xs:hidden">
                <button className="hidden btn-main bg-purple-120 mt-2 text-white font-semibold px-12 py-3  rounded-full">
                  Ver todos
                </button>
              </div>
            </div>
            {sanity.gridType == 'Display - Colecciones pequeño' ? (
              <div
                className={`excollection_small_container_carousel_wrapper contains_${sanity.collections.length}`}
              >
                <div
                  className="excollection_small_container_carousel overflow-x-hidden"
                  ref={viewportRef}
                >
                  {sanity.collections && (
                    <div className="excollection_container">
                      {sanity.collections.map((item, index) => (
                        <Link
                          className="embla_slide rounded-md  shadow-md bg-white mt-8 xs:bg-gray-65"
                          to={getPath(item)}
                          key={'link-' + index}
                        >
                          <div className="bg-image-wrapper">
                            <div
                              className="image-container"
                              style={{
                                background:
                                  item.thumbbackgroundType == 'Solid'
                                    ? item.thumbbackgroundColor
                                    : item.thumbbackgroundType == 'Image'
                                    ? `url(${item.thumbbackgroundImage.asset.url})`
                                    : item.thumbbackgroundType == 'Gradient'
                                    ? `linear-gradient(90deg, ${item?.thumbbackgroundGradient?.gradientFrom} 0%, ${sanity?.thumbbackgroundGradient?.gradientTo} 100%)`
                                    : '#00000',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                              }}
                            ></div>
                          </div>
                          <div className="image-wrapper">
                            {item.featuredImage?.asset.url ? (
                              <Image
                                src={item.featuredImage?.asset.url}
                                height={'50%'}
                                width={'auto'}
                                alt={item.title}
                                className="featured-image"
                              />
                            ) : (
                              ''
                            )}
                          </div>
                          <div className="card-content bg-white px-3 py-3 rounded-br-md rounded-bl-md xs:bg-gray-65 xs:py-2">
                            <h3 className="text-purple-120 font-semibold card-title xs:mb-1">
                              {item.title}
                            </h3>
                            <p>
                              {generateTextblock(item.pageTitle).slice(0, 200)}
                              ...
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
                <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
              </div>
            ) : !isMobile ? (
              sanity.collections &&
              sanity.collections.map((item) => (
                <Link
                  to={getPath(item)}
                  className="collecciones_card"
                  key={'col-' + item._key}
                >
                  <div className="card bg-white mt-9">
                    <div className="bg-image-wrapper">
                      <div
                        className="image-container"
                        style={{
                          background:
                            item.thumbbackgroundType == 'Solid'
                              ? item.thumbbackgroundColor
                              : item.thumbbackgroundType == 'Image'
                              ? `url(${item.thumbbackgroundImage.asset.url})`
                              : item.thumbbackgroundType == 'Gradient'
                              ? `linear-gradient(90deg, ${item?.thumbbackgroundGradient?.gradientFrom} 0%, ${sanity?.thumbbackgroundGradient?.gradientTo} 100%)`
                              : '#00000',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      ></div>
                    </div>
                    <div className="image-wrapper">
                      {item.featuredImage?.asset.url ? (
                        <Image
                          src={item.featuredImage?.asset.url}
                          height={'50%'}
                          width={'auto'}
                          alt={item.title}
                          className="featured-image"
                        />
                      ) : (
                        ''
                      )}
                    </div>
                    <div className="card-content bg-white py-3 px-3 rounded-br-md rounded-bl-md flex flex-col xs:py-2 xs:bg-gray-65">
                      <h3 className="text-purple-120 font-semibold card-title xs:mb-1">
                        {item.title}
                      </h3>
                      <p>
                        {generateTextblock(item.pageTitle).slice(0, 200)}...
                      </p>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="excollection_small_container_carousel_wrapper">
                <div
                  className="excollection_small_container_carousel overflow-x-hidden"
                  ref={viewportRef}
                >
                  {sanity.collections && (
                    <div className="excollection_container">
                      {sanity.collections.map((item) => (
                        <Link
                          className="embla_slide rounded-md  shadow-md bg-white mt-8 xs:bg-gray-65"
                          key={item._key}
                          to={getPath(item)}
                        >
                          <div className="bg-image-wrapper">
                            <div
                              className="image-container"
                              style={{
                                background:
                                  item.thumbbackgroundType == 'Solid'
                                    ? item.thumbbackgroundColor
                                    : item.thumbbackgroundType == 'Image'
                                    ? `url(${item.thumbbackgroundImage.asset.url})`
                                    : item.thumbbackgroundType == 'Gradient'
                                    ? `linear-gradient(90deg, ${item?.thumbbackgroundGradient?.gradientFrom} 0%, ${sanity?.thumbbackgroundGradient?.gradientTo} 100%)`
                                    : '#00000',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                              }}
                            ></div>
                          </div>
                          <div className="image-wrapper">
                            {item.featuredImage?.asset.url ? (
                              <Image
                                src={item.featuredImage?.asset.url}
                                height={'50%'}
                                width={'auto'}
                                alt={item.title}
                                className="featured-image"
                              />
                            ) : (
                              ''
                            )}
                          </div>
                          <div className="card-content bg-white px-3 py-3 rounded-br-md rounded-bl-md xs:bg-gray-65 xs:py-2">
                            <h3 className="text-purple-120 font-semibold card-title xs:mb-1">
                              {item.title}
                            </h3>
                            <p>
                              {generateTextblock(item.pageTitle).slice(0, 200)}
                              ...
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
                <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
                <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
              </div>
            )}

            <div className="hidden xs:flex justify-center">
              <button className="btn-main bg-purple-120 mt-2 mb-0 inline text-white font-semibold px-12 rounded-full">
                Ver todos
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {sanity?.gridBannerMainBanner && (
        <div className="container">
          <div className="section_librero">
            <div className="md:mx-auto flex flex-wrap xs:flex-col">
              <div className="md:w-6/12 flex md:px-0 flex-col xs:w-full">
                <div className="w-full flex items-end xs:justify-between xs:items-end xs:content-end xs:leading-none xs:px-4">
                  {sanity.titleType == 'Image' ? (
                    <>
                      <ImageLoad
                        src={sanity?.imageTitle?.asset.url}
                        alt={sanity.title}
                        width={'auto'}
                        height={'auto'}
                        className="inline mr-2 xs:w-52"
                      />
                      <Link
                        to={getPath(sanity.gridBannerMainBanner)}
                        className="hidden"
                      >
                        Ver todos
                      </Link>
                    </>
                  ) : (
                    <>
                      <h2 className="section-title text-white">
                        {sanity.title}
                      </h2>
                      <Link
                        to={getPath(sanity.gridBannerMainBanner)}
                        className="hidden"
                      >
                        Ver todos
                      </Link>
                    </>
                  )}
                </div>
                <div className="w-full my-5 item-center justify-center text-center xs:px-10">
                  <div className="text-center">
                    {sanity.gridBannerMainBanner?.image && (
                      <ImageLoad
                        src={
                          (browserWidth < 576)
                          ? sanity.gridBannerMainBanner.image.asset.url+'?fm=webp&h=420&w=240'
                          : sanity.gridBannerMainBanner.image.asset.url
                        }
                        alt={sanity.title}
                        className="rounded-lg mx-auto colecciones_image"
                      />
                    )}
                  </div>
                  <div className="xs:pt-4">
                    <h3 className="font-semibold text-2xl text-white w-full xs:pb-5 mb-3">
                      {sanity.gridBannerMainBanner.title}
                    </h3>
                    
                    <Link
                      className="btn-main bg-purple-120 mt-2 text-white font-semibold pb-3 rounded-full inline-flex"
                      to={getPath(
                        {
                          ...sanity.gridBannerMainBanner,
                          redirectionType:
                            sanity.gridBannerMainBanner.redirectionTypeMain,
                        },
                        'here',
                      )}
                      target={(sanity.gridBannerMainBanner.redirectionTypeMain === 'Downloadable') ? '_blank' : '_parent'}
                    >
                      {sanity.gridBannerMainBanner.buttonText}
                    </Link>

                  </div>
                </div>
              </div>
              <div className="md:w-6/12 md:px-10 xs:w-full">
                {sanity.gridBannerFeatured && (
                  <div className="xs:visible md:hidden">
                    <GridCarousel list={sanity.gridBannerFeatured} />
                  </div>
                )}
                {sanity.gridBannerFeatured && (
                  <div className="w-full flex flex-wrap gap-5 xs:hidden md:visible">
                    {sanity.gridBannerFeatured.map((item) => (
                      <div
                        className="md:w-5/12 xs:w-full"
                        key={'gridbannerFeatured-' + item._key}
                      >
                        <Link to={getPath(item, 'here is ths one')} reloadDocument="true">
                          <div className="card relative overflow-hidden">
                            <h3 className="font-semibold text-left text-2xl text-white  w-full ">
                              {item.title}
                            </h3>
                            <div className="card-image-wrapper">
                              <Image
                                src={item.image.asset.url+'?fm=webp&h=350'}
                                height={227}
                                width={'100%'}
                                alt={item.title}
                                className=""
                              />
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {sanity?.gridBlogFeatured && (
        <div
          className="grid_blog_featured"
          style={{
            background:
              sanity.backgroundType == 'Solid'
                ? sanity.backgroundColor
                : sanity.backgroundType == 'Image'
                ? `url(${sanity.backgroundImage.asset.url})`
                : sanity.backgroundType == 'Gradient'
                ? '#0000' //`linear-gradient(90deg, ${sanity?.backgroundGradient.gradientFrom} 0%, ${sanity?.backgroundGradient.gradientTo} 100%)`
                : '#00000',
            backgroundSize: 'cover',
          }}
        >
          <div className="w-full">
            {sanity.gridBlogFeatured &&
              sanity.gridBlogFeatured.map((item, key) => {
                if (key == 0) {
                  return (
                    <div className=" rounded-xl" key={item._key}>
                      <Link
                        to={getPath(item, 'featured')}
                        className="bg-white shadow-md relative pb-5 "
                      >
                        <div className="rounded-full px-5 m-2 font-semibold inline text-white bg-purple-120 absolute left-0">
                          {item.tag}
                        </div>
                        <div
                          className="w-full h-60"
                          style={{
                            backgroundSize: 'cover !important',
                            backgroundRepeat: 'no-repeat !important',
                            background: `url(${item.bannerImage.asset.url})`,
                          }}
                        ></div>

                        <p className="p-5 text-2xl font-semibold text-purple-120 text-center">
                          {item.title}
                        </p>
                        <p className="pb-5 px-5 text-center">
                          {item.description}
                        </p>
                      </Link>
                    </div>
                  );
                }
              })}
          </div>
          <div className="container mx-auto mt-5">
            <h2 className="section-title">{sanity.title}</h2>
            <div className="product_carousel_wrapper px-12 mx-auto relative xs:px-0">
              <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
              <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
              <div className="carousel_inner overflow-hidden">
                <div ref={viewportRef}>
                  <div className="embla__container">
                    {sanity.gridBlogFeatured &&
                      sanity.gridBlogFeatured.map((item, key) => {
                        if (key > 0) {
                          return (
                            <div
                              className="md:embla__slide--thumb product_slide xs:w-full relative "
                              key={item._key}
                            >
                              <div className="bg-white shadow-md relative pb-5 rounded-md ">
                                <div className="rounded-full px-5 m-2 font-semibold inline text-white bg-purple-120 absolute left-0">
                                  {item.tag}
                                </div>
                                <div
                                  className="w-full h-60 rounded-tl-md rounded-tr-md"
                                  style={{
                                    backgroundSize: 'cover !important',
                                    backgroundRepeat: 'no-repeat !important',
                                    background: `url(${item.featuredImage.asset.url})`,
                                  }}
                                ></div>

                                <Link
                                  to={getPath(item)}
                                  className=" font-semibold text-purple-120 text-left  w-full block px-3 pb-5"
                                >
                                  {item.title}
                                </Link>
                                <p className="pb-5 px-5">{item.description}</p>
                              </div>
                            </div>
                          );
                        }
                      })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {sanity?.gridPostLarge && (
        <div
          className="container grid_post_large"
          style={{
            background:
              sanity.backgroundType == 'Solid'
                ? sanity.backgroundColor
                : sanity.backgroundType == 'Image'
                ? `url(${sanity.backgroundImage.asset.url})`
                : sanity.backgroundType == 'Gradient'
                ? `linear-gradient(90deg, ${sanity?.backgroundGradient?.gradientFrom} 0%, ${sanity?.backgroundGradient?.gradientTo} 100%)`
                : '#00000',
            backgroundSize: 'cover',
          }}
        >
          <div className="mx-auto flex flex-wrap">
            <div className="w-full flex justify-center items-center content-center mb-8">
              <h2 className="section-title">{sanity.title}</h2>
            </div>
            <div className="w-full">
              <div className="grid grid-cols-2 gap-6">
                {sanity.gridPostLarge
                  ? sanity.gridPostLarge.map((item) => {
                      return (
                        <div
                          className="shadow-md rounded-xl  bg-white "
                          key={item._key}
                        >
                          <Link
                            to={getPath(item)}
                            className="blog-card card-large bg-white shadow-md relative pb-5 "
                          >
                            <div className="rounded-full px-5 m-2 font-semibold inline text-white bg-purple-120 absolute left-0">
                              {item.tag}
                            </div>
                            <div
                              className="w-full h-80"
                              style={{
                                backgroundSize: 'cover !important',
                                backgroundRepeat: 'no-repeat !important',
                                background: `url(${item.featured.featuredImage.asset.url})`,
                              }}
                            ></div>
                            <div className="card-content-wrapper">
                              <div className="p-3">
                                <h3 className="card-title text-purple-120 text-center xs:mb-2">
                                  {item.featured.title}
                                </h3>
                                <p className="card-content text-center">
                                  {item.featured.description}
                                </p>
                              </div>
                            </div>
                          </Link>
                        </div>
                      );
                    })
                  : ''}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
